import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  SearchInput,
  Badge,
  ScrollArea,
  toast,
} from '@aaas/shared';
import { Briefcase, Loader2, Info, X } from 'lucide-react';
import { useJobListQuery } from '../hooks/useJobQueries';

interface SelectedJobInfo {
  id: string;
  code: string;
  positionName: string;
}

interface AddCVToJobDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (jobIds: string[]) => Promise<void>;
  workspaceId: string | undefined;
  idToken: string | undefined;
  cvId: string;
}

export function AddCVToJobDialog({
  open,
  onClose,
  onConfirm,
  workspaceId,
  idToken,
  cvId,
}: AddCVToJobDialogProps) {
  const { t } = useTranslation('ats');
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [selectedJobsMap, setSelectedJobsMap] = useState<Map<string, SelectedJobInfo>>(new Map());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch jobs with search support
  const { data: jobsData, isLoading: jobsLoading } = useJobListQuery({
    workspaceId,
    idToken,
    page: 1,
    size: 50,
    status: 'open',
    search: searchQuery,
    excludeCvId: cvId, // Exclude jobs that already have this CV
    enabled: open,
    refetchInterval: false,
  });

  // Use jobs directly from API (search is now server-side, jobs already excluded on backend)
  const allJobs = jobsData?.data.items || [];

  // Reset selections when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSelectedJobIds(new Set());
      setSelectedJobsMap(new Map());
      setSearchQuery('');
      setError(null);
    }
  }, [open]);

  const handleToggleJob = useCallback((jobId: string, code: string, positionName: string) => {
    setSelectedJobIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
    setSelectedJobsMap((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(jobId)) {
        newMap.delete(jobId);
      } else {
        newMap.set(jobId, { id: jobId, code, positionName });
      }
      return newMap;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    const allVisible = allJobs.every((job) => selectedJobIds.has(job.id));
    if (allVisible) {
      // Deselect only visible jobs
      setSelectedJobIds((prev) => {
        const newSet = new Set(prev);
        for (const job of allJobs) {
          newSet.delete(job.id);
        }
        return newSet;
      });
      setSelectedJobsMap((prev) => {
        const newMap = new Map(prev);
        for (const job of allJobs) {
          newMap.delete(job.id);
        }
        return newMap;
      });
    } else {
      // Select all visible jobs
      setSelectedJobIds((prev) => {
        const newSet = new Set(prev);
        for (const job of allJobs) {
          newSet.add(job.id);
        }
        return newSet;
      });
      setSelectedJobsMap((prev) => {
        const newMap = new Map(prev);
        for (const job of allJobs) {
          if (!newMap.has(job.id)) {
            newMap.set(job.id, { id: job.id, code: job.code, positionName: job.position.name });
          }
        }
        return newMap;
      });
    }
  }, [allJobs, selectedJobIds]);

  const handleRemoveSelected = useCallback((jobId: string) => {
    setSelectedJobIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(jobId);
      return newSet;
    });
    setSelectedJobsMap((prev) => {
      const newMap = new Map(prev);
      newMap.delete(jobId);
      return newMap;
    });
  }, []);

  const handleConfirm = async () => {
    if (selectedJobIds.size === 0) {
      setError(t('addCvToJob.selectAtLeastOne'));
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm(Array.from(selectedJobIds));

      // Show success toast
      toast.success(
        t('addCvToJob.toast.success', { count: selectedJobIds.size })
      );

      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('addCvToJob.addFailed');
      setError(errorMessage);

      // Show error toast
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const allVisibleSelected = allJobs.length > 0 && allJobs.every((job) => selectedJobIds.has(job.id));

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5 text-blue-500" />
            {t('addCvToJob.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Selected Jobs Section */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm font-medium text-blue-900 mb-2">
              {t('addCvToJob.selectedJobs', { count: selectedJobIds.size })}
            </p>
            <div className="h-8">
              <ScrollArea className="h-8">
                {selectedJobIds.size > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {Array.from(selectedJobsMap.values()).map((jobInfo) => (
                      <Badge
                        key={jobInfo.id}
                        variant="secondary"
                        className="bg-blue-100 text-blue-900 hover:bg-blue-200 pr-1 py-1"
                      >
                        <span className="mr-1">{jobInfo.code} - {jobInfo.positionName}</span>
                        <button
                          onClick={() => handleRemoveSelected(jobInfo.id)}
                          className="inline-flex items-center justify-center rounded-full hover:bg-blue-300 p-0.5"
                          aria-label={t('addCvToJob.removeSelectedAria', { name: jobInfo.code })}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 w-full h-full flex items-center justify-center">
                    {t('addCvToJob.noJobsSelected')}
                  </p>
                )}
              </ScrollArea>
            </div>
          </div>

          {/* Job Selection */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Label>{t('addCvToJob.selectJobs')}</Label>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="h-4 w-4 text-blue-500 cursor-pointer" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-sm">
                        {t('addCvToJob.tooltip')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {allJobs.length > 0 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleAll}
                  className="text-xs"
                >
                  {allVisibleSelected
                    ? t('addCvToJob.deselectAll')
                    : t('addCvToJob.selectAll')}
                </Button>
              )}
            </div>

            {/* Search Input */}
            <SearchInput
              placeholder={t('addCvToJob.searchPlaceholder')}
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              width="xl"
              className="w-full"
              showClearButton={true}
            />

            {/* Job List */}
            {jobsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
              </div>
            ) : allJobs.length > 0 ? (
              <div className="border border-gray-200 rounded-lg">
                <ScrollArea className="h-60">
                  <>
                    {allJobs.map((job) => {
                      const isSelected = selectedJobIds.has(job.id);

                      return (
                        <label
                          key={job.id}
                          className="flex items-start gap-3 p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer"
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleToggleJob(job.id, job.code, job.position.name)}
                            className="mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">
                              {job.code} - {job.position.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {job.campaign.name}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </>
                </ScrollArea>
              </div>
            ) : searchQuery ? (
              <p className="text-sm text-gray-500 text-center py-8 h-60">
                {t('addCvToJob.noMatchFound', { query: searchQuery })}
              </p>
            ) : (
              <p className="text-sm text-gray-500 text-center py-8 h-60">
                {t('addCvToJob.noOpenJobs')}
              </p>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('addCvToJob.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedJobIds.size === 0 || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('addCvToJob.adding')}
              </>
            ) : (
              t('addCvToJob.addToJobs', { count: selectedJobIds.size })
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
